import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { Encomenda, CriarEncomendaRequest, AtualizarEncomendaRequest } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

interface ListarEncomendasParams {
  pagina?: number;
  tamanhoPagina?: number;
  dataFiltro?: string;
}

interface ListarEncomendasResponse {
  encomendas: Encomenda[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

export const useEncomendas = (params?: ListarEncomendasParams) => {
  return useQuery<ListarEncomendasResponse>({
    queryKey: ['encomendas', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.pagina) queryParams.append('pagina', params.pagina.toString());
      if (params?.tamanhoPagina) queryParams.append('tamanhoPagina', params.tamanhoPagina.toString());
      if (params?.dataFiltro) queryParams.append('dataFiltro', params.dataFiltro);

      const response = await api.get(`/encomendas?${queryParams.toString()}`);
      return response.data;
    },
  });
};

export const useEncomenda = (id: number) => {
  return useQuery<Encomenda>({
    queryKey: ['encomenda', id],
    queryFn: async () => {
      const response = await api.get(`/encomendas/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCriarEncomenda = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CriarEncomendaRequest) => {
      const response = await api.post('/encomendas', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encomendas'] });
      toast.success('Encomenda criada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao criar encomenda'));
    },
  });
};

export const useAtualizarEncomenda = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AtualizarEncomendaRequest }) => {
      const response = await api.put(`/encomendas/${id}`, {
        id,
        ...data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encomendas'] });
      queryClient.invalidateQueries({ queryKey: ['encomenda'] });
      toast.success('Encomenda atualizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar encomenda'));
    },
  });
};

export const useRemoverEncomenda = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/encomendas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encomendas'] });
      toast.success('Encomenda removida com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao remover encomenda'));
    },
  });
};

export const useAlterarStatusEncomenda = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await api.patch(`/encomendas/${id}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encomendas'] });
      queryClient.invalidateQueries({ queryKey: ['encomenda'] });
      toast.success('Status alterado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao alterar status'));
    },
  });
};
